import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, AlertTriangle, TrendingUp, Clock, Award,
  ChevronRight, Link2, Loader2, FileText, Camera, BarChart3,
  Calendar, Zap, Target, Send, Bell, X, Info, Plus, 
  BookOpen, Flame, Settings, ChevronDown
} from 'lucide-react';
import DailyReminderModal from '../components/DailyReminderModal';

const subjectIcons = { 
  Math: '📐', 
  Science: '🔬', 
  English: '📖', 
  'Social Science': '🌍', 
  Hindi: '📚', 
  Physics: '⚛️', 
  Chemistry: '🧪', 
  Biology: '🧬' 
};

export default function ParentDashboard({ onNavigate }) {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [childEmail, setChildEmail] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkMsg, setLinkMsg] = useState('');
  const [showReminder, setShowReminder] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [showFirstTooltip, setShowFirstTooltip] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [activeChild, setActiveChild] = useState(0);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/parent/dashboard');
      setDashboard(res.data);
      
      if (res.data?.children?.length > 0) {
        const childId = res.data.children[0].child.id;
        
        try {
          const [alertsRes, summaryRes] = await Promise.all([
            api.get(`/parent/child-alerts/${childId}`),
            api.get(`/parent/weekly-summary/${childId}`)
          ]);
          setAlerts(alertsRes.data?.alerts || []);
          setWeeklySummary(summaryRes.data);
        } catch (err) {
          console.debug('Failed to fetch alerts/summary:', err);
        }
      }
      
      try {
        const tooltipsRes = await api.get('/user/tooltips-status');
        if (!tooltipsRes.data?.tooltips_seen?.parent_dashboard) {
          setTimeout(() => setShowFirstTooltip(true), 2000);
        }
      } catch (err) { console.debug('Tooltip fetch skipped'); }
      
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    const checkReminder = async () => {
      const lastDismiss = localStorage.getItem('studyclaw_parent_reminder_dismiss');
      const today = new Date().toDateString();
      const lastDismissDate = lastDismiss ? new Date(parseInt(lastDismiss)).toDateString() : null;
      
      if (lastDismissDate === today) return;
      
      try {
        const examsRes = await api.get('/exams/subject-wise');
        if (!examsRes.data || examsRes.data.length === 0) {
          setTimeout(() => setShowReminder(true), 2000);
        }
      } catch (err) {
        console.debug('Failed to check reminder');
      }
    };
    
    if (!loading) checkReminder();
  }, [api, loading]);

  const handleCloseReminder = () => {
    setShowReminder(false);
    localStorage.setItem('studyclaw_parent_reminder_dismiss', new Date().getTime().toString());
  };

  const linkChild = async () => {
    if (!childEmail.trim()) return;
    setLinking(true);
    setLinkMsg('');
    try {
      await api.post('/user/link-child', { child_email: childEmail });
      setLinkMsg('✓ Child linked successfully!');
      setChildEmail('');
      setShowLinkModal(false);
      fetchDashboard();
    } catch (err) {
      setLinkMsg(`✗ ${err.response?.data?.detail || 'Failed to link child'}`);
    }
    setLinking(false);
  };

  const dismissAlert = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
  };

  const dismissTooltip = async () => {
    setShowFirstTooltip(false);
    try {
      await api.post('/user/tooltip-seen?tooltip_id=parent_dashboard');
    } catch (e) { console.debug('Tooltip mark failed'); }
  };

  const child = dashboard?.children?.[activeChild]?.child;
  const childStats = dashboard?.children?.[activeChild];
  const activeAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center mx-auto mb-3 animate-pulse">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // No children linked
  if (!dashboard?.children || dashboard.children.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 pt-6 pb-24" data-testid="parent-dashboard">
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">Parent Dashboard</h1>
          <p className="text-slate-400 mb-8">Link your child&apos;s account to start tracking their progress</p>
          
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
            <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-8 h-8 text-violet-400" />
            </div>
            <h2 className="text-lg font-bold text-white text-center mb-2">Link Your Child</h2>
            <p className="text-sm text-slate-400 text-center mb-6">
              Enter your child&apos;s StudyClaw email to connect accounts
            </p>
            
            <input
              type="email"
              value={childEmail}
              onChange={(e) => setChildEmail(e.target.value)}
              placeholder="child@email.com"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-violet-500 outline-none mb-3"
              data-testid="link-child-email"
            />
            
            <button
              onClick={linkChild}
              disabled={linking || !childEmail.trim()}
              className="w-full bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-all"
              data-testid="link-child-btn"
            >
              {linking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Link2 className="w-5 h-5" />}
              Link Child Account
            </button>
            
            {linkMsg && (
              <p className={`text-sm mt-3 text-center ${linkMsg.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`}>
                {linkMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="parent-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <p className="text-xs text-slate-500">Parent Dashboard</p>
            <h1 className="text-lg font-bold text-white">{user?.name || 'Parent'}</h1>
          </div>
          <button
            onClick={() => onNavigate('profile')}
            className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
          >
            <Settings className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* Child Selector (if multiple children) */}
        {dashboard.children.length > 1 && (
          <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
            {dashboard.children.map((c, i) => (
              <button
                key={c.child.id || c.child.email || `child-${i}`}
                onClick={() => setActiveChild(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeChild === i
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {c.child.name}
              </button>
            ))}
            <button
              onClick={() => setShowLinkModal(true)}
              className="px-3 py-2 rounded-full bg-slate-800/50 border border-dashed border-slate-700 text-slate-500 hover:border-slate-600 transition-all"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Child Overview Card */}
        <div className="bg-gradient-to-br from-violet-600/20 to-cyan-600/10 border border-violet-500/20 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-violet-600/30 flex items-center justify-center text-xl">
                {child?.name?.charAt(0) || '👤'}
              </div>
              <div>
                <h2 className="font-bold text-white">{child?.name || 'Student'}</h2>
                <p className="text-xs text-slate-400">{child?.class_name} • {child?.board || 'CBSE'}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-orange-400">
                <Flame className="w-4 h-4" />
                <span className="font-bold">{childStats?.streak || 0}</span>
              </div>
              <p className="text-[10px] text-slate-500">day streak</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-900/50 rounded-xl p-3 text-center">
              <div className="text-lg font-bold text-white">{childStats?.sessions_this_week || 0}</div>
              <p className="text-[10px] text-slate-500">Sessions/Week</p>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-3 text-center">
              <div className="text-lg font-bold text-white">{childStats?.xp || 0}</div>
              <p className="text-[10px] text-slate-500">Total XP</p>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-3 text-center">
              <div className={`text-lg font-bold ${
                (childStats?.readiness_score || 0) >= 70 ? 'text-emerald-400' : 
                (childStats?.readiness_score || 0) >= 50 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {childStats?.readiness_score || 0}%
              </div>
              <p className="text-[10px] text-slate-500">Readiness</p>
            </div>
          </div>
        </div>

        {/* Alerts Section */}
        {activeAlerts.length > 0 && (
          <div className="mb-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-400" />
              Alerts
            </h3>
            <div className="space-y-2">
              {activeAlerts.slice(0, 3).map((alert, i) => (
                <motion.div
                  key={alert.id || i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${
                    alert.type === 'critical' 
                      ? 'bg-red-500/10 border-red-500/30' 
                      : alert.type === 'warning'
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : 'bg-slate-800/50 border-slate-700'
                  }`}
                >
                  <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                    alert.type === 'critical' ? 'text-red-400' : 'text-amber-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{alert.message}</p>
                    <p className="text-xs text-slate-500 mt-1">{alert.time_ago || 'Recently'}</p>
                  </div>
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="text-slate-500 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* First-use Tooltip */}
        <AnimatePresence>
          {showFirstTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-5 relative"
            >
              <button onClick={dismissTooltip} className="absolute top-2 right-2 text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white mb-1">Your child&apos;s Readiness Score updates after every session.</p>
                  <p className="text-sm text-slate-400">If it drops below 60% within a week of an exam, we&apos;ll alert you automatically.</p>
                  <button onClick={dismissTooltip} className="text-violet-400 text-sm font-medium mt-2 hover:text-violet-300">
                    Got it
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions */}
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-white mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              onClick={() => onNavigate('parent-test-generator')}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="create-test-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-violet-400" />
              </div>
              <h4 className="font-medium text-white text-sm">Create Test</h4>
              <p className="text-xs text-slate-500">Generate custom tests</p>
            </motion.button>

            <motion.button
              onClick={() => onNavigate('answer-sheet-evaluator')}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="evaluate-sheet-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center mb-3">
                <Camera className="w-5 h-5 text-pink-400" />
              </div>
              <h4 className="font-medium text-white text-sm">Grade Answers</h4>
              <p className="text-xs text-slate-500">AI answer evaluator</p>
            </motion.button>

            <motion.button
              onClick={() => onNavigate('school-score-tracker')}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="track-scores-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-3">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
              </div>
              <h4 className="font-medium text-white text-sm">Track Scores</h4>
              <p className="text-xs text-slate-500">Upload report cards</p>
            </motion.button>

            <motion.button
              onClick={() => onNavigate('exam-calendar')}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="exam-calendar-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center mb-3">
                <Calendar className="w-5 h-5 text-amber-400" />
              </div>
              <h4 className="font-medium text-white text-sm">Exam Calendar</h4>
              <p className="text-xs text-slate-500">View upcoming exams</p>
            </motion.button>
          </div>
        </div>

        {/* Weekly Summary */}
        {weeklySummary && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-5">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              Weekly Summary
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-2xl font-bold text-white">{weeklySummary.sessions_completed || 0}</p>
                <p className="text-xs text-slate-500">Sessions completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{weeklySummary.quizzes_taken || 0}</p>
                <p className="text-xs text-slate-500">Quizzes taken</p>
              </div>
              <div>
                <p className={`text-2xl font-bold ${
                  (weeklySummary.avg_score || 0) >= 70 ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {weeklySummary.avg_score || 0}%
                </p>
                <p className="text-xs text-slate-500">Avg quiz score</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-violet-400">+{weeklySummary.xp_gained || 0}</p>
                <p className="text-xs text-slate-500">XP earned</p>
              </div>
            </div>

            {weeklySummary.weak_topics && weeklySummary.weak_topics.length > 0 && (
              <div className="pt-4 border-t border-slate-800">
                <p className="text-xs text-slate-500 mb-2">Focus areas this week:</p>
                <div className="flex flex-wrap gap-2">
                  {weeklySummary.weak_topics.slice(0, 3).map((topic) => (
                    <span key={topic} className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded-full">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subject Progress */}
        {childStats?.subject_progress && childStats.subject_progress.length > 0 && (
          <div className="mb-5">
            <h3 className="text-sm font-semibold text-white mb-3">Subject Progress</h3>
            <div className="space-y-3">
              {childStats.subject_progress.map((subject) => (
                <div key={subject.name} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{subjectIcons[subject.name] || '📘'}</span>
                      <span className="font-medium text-white text-sm">{subject.name}</span>
                    </div>
                    <span className={`text-sm font-semibold ${
                      subject.score >= 70 ? 'text-emerald-400' : 
                      subject.score >= 50 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {subject.score}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        subject.score >= 70 ? 'bg-emerald-500' : 
                        subject.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${subject.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800 px-4 py-2 z-50">
        <div className="max-w-lg mx-auto flex justify-around">
          {[
            { icon: '🏠', label: 'Home', page: 'parent-dashboard', active: true },
            { icon: '📝', label: 'Tests', page: 'parent-test-generator' },
            { icon: '📊', label: 'Scores', page: 'school-score-tracker' },
            { icon: '📅', label: 'Exams', page: 'exam-calendar' },
            { icon: '👤', label: 'Profile', page: 'profile' },
          ].map((item) => (
            <button
              key={item.page}
              onClick={() => onNavigate(item.page)}
              className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-all ${
                item.active ? 'text-violet-400' : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.active && <div className="w-1 h-1 rounded-full bg-violet-500 mt-0.5" />}
            </button>
          ))}
        </div>
      </nav>

      {/* Link Child Modal */}
      <AnimatePresence>
        {showLinkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLinkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-sm"
            >
              <h3 className="text-lg font-bold text-white mb-4">Link Another Child</h3>
              <input
                type="email"
                value={childEmail}
                onChange={(e) => setChildEmail(e.target.value)}
                placeholder="child@email.com"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-violet-500 outline-none mb-3"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="flex-1 bg-slate-800 text-slate-300 rounded-xl py-3 font-medium hover:bg-slate-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={linkChild}
                  disabled={linking || !childEmail.trim()}
                  className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 text-white rounded-xl py-3 font-medium transition-all"
                >
                  {linking ? 'Linking...' : 'Link'}
                </button>
              </div>
              {linkMsg && (
                <p className={`text-sm mt-3 text-center ${linkMsg.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`}>
                  {linkMsg}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Daily Reminder Modal */}
      <DailyReminderModal 
        isOpen={showReminder} 
        onClose={handleCloseReminder}
        onStartSession={() => { handleCloseReminder(); onNavigate('teacher-dashboard'); }}
        isParent={true}
      />
    </div>
  );
}
